import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOMeta from '@/components/feature/SEOMeta';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Hitozuma 专题导览',
    url: window.location.origin,
    description: '致力于为 hitozuma 文化研究者和爱好者提供全面、专业的内容导览与资源索引。',
    email: 'contact@hitozuma-guide.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: '东京都',
      addressCountry: 'JP',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@hitozuma-guide.com',
      availableLanguage: ['Japanese', 'Chinese'],
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qsoqsnlsngrm4lm5cg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([key, value]) => [key, value.toString()])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg =
          parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后再试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title="关于我们 | 品牌介绍、口碑与联系方式 - Hitozuma 专题导览"
        description="了解 Hitozuma 专题导览的品牌故事、核心团队、内容理念与联系方式。我们致力于为 hitozuma 文化研究者与爱好者提供专业、全面、客观的内容服务。"
        keywords="hitozuma,hitozuma 专题,关于我们,联系方式,品牌介绍"
        canonicalUrl="/about"
        schema={orgSchema}
      />
      <Navbar />

      <main className="pt-16">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
        </div>

        {/* Brand Introduction */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="px-4 md:px-6 max-w-4xl mx-auto">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 text-center mb-4">
              关于 Hitozuma 专题导览
            </h1>
            <p className="text-center text-foreground-600 mb-12 max-w-2xl mx-auto leading-relaxed">
              Hitozuma 专题导览是一个专注于 hitozuma 文化深度解析的专业平台。我们汇聚了一群对日本文化有深厚研究背景的编辑与撰稿人，致力于为全球华语用户提供全面、客观、专业的 hitozuma 文化内容导览与资源索引服务。
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  icon: 'ri-lightbulb-line',
                  title: '专业深度',
                  desc: '每一篇文章都由具有学术背景的编辑精心撰写，确保内容的准确性与专业性。',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '客观立场',
                  desc: '坚持客观、中立的分析视角，尊重多元文化观点，推动健康的文化交流与讨论。',
                },
                {
                  icon: 'ri-global-line',
                  title: '全面覆盖',
                  desc: '内容涵盖基础入门、作品解析、文化背景、分类盘点及资源索引五大维度。',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="p-6 rounded-xl bg-background-50 border border-background-200/60 text-center"
                >
                  <div className="w-12 h-12 mx-auto mb-4 rounded-lg bg-accent-100 flex items-center justify-center">
                    <i className={`${item.icon} text-xl text-accent-600`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation / Stats */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
              品牌口碑与数据
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { label: '深度文章', value: '40+' },
                { label: '内容分类', value: '5' },
                { label: '资源索引', value: '100+' },
                { label: '月活跃用户', value: '50K+' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-5">
                  <div className="text-3xl md:text-4xl font-bold text-accent-600 font-heading">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-600 mt-2">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  name: '张教授',
                  role: '日本文化研究者',
                  quote: 'Hitozuma 专题导览是目前华语互联网上关于 hitozuma 文化最全面、最专业的资源平台之一。文章质量高，分析视角独到。',
                },
                {
                  name: '李编辑',
                  role: '文化媒体从业者',
                  quote: '作为同行，我很欣赏这个平台的内容深度和编辑态度。不仅信息量大，更难能可贵的是保持了学术严谨性和可读性的平衡。',
                },
                {
                  name: '王先生',
                  role: '长期读者',
                  quote: '从入门到深入，这个网站陪我走过了对 hitozuma 文化从好奇到理解的整个过程。内容分类清晰，推荐给所有感兴趣的朋友。',
                },
                {
                  name: '赵女士',
                  role: '文化评论人',
                  quote: '在众多泛娱乐化的内容中，Hitozuma 专题导览保持了难得的专业性与深度，是真正值得信赖的文化研究参考。',
                },
              ].map((testimonial) => (
                <div
                  key={testimonial.name}
                  className="p-6 rounded-xl bg-background-50 border border-background-200/60"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-accent-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{testimonial.quote}&rdquo;
                  </p>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{testimonial.name}</p>
                    <p className="text-xs text-foreground-500">{testimonial.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="px-4 md:px-6 max-w-2xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-4">
              联系我们
            </h2>
            <p className="text-center text-foreground-600 mb-10 leading-relaxed">
              如有任何问题、建议或合作意向，欢迎通过以下表单与我们取得联系。我们会在 48 小时内回复。
            </p>

            <form onSubmit={handleSubmit} data-readdy-form="">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题 <span className="text-primary-500">*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                  placeholder="请输入主题"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                  placeholder="请输入留言内容（限500字以内）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">限 500 字以内</p>
              </div>

              <div className="honeypot-field">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <button
                type="submit"
                className="w-full px-8 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                发送留言
              </button>

              {formStatus === 'success' && (
                <div className="mt-4 p-4 rounded-lg bg-accent-100 text-accent-800 text-sm text-center">
                  感谢您的留言！我们会在 48 小时内回复您。
                </div>
              )}

              {formStatus === 'error' && (
                <div className="mt-4 p-4 rounded-lg bg-primary-100 text-primary-800 text-sm text-center">
                  {formError}
                </div>
              )}
            </form>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-center gap-3 p-4 rounded-lg bg-background-100">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-accent-600 w-4 h-4 flex items-center justify-center"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">邮箱</p>
                  <p className="text-sm font-medium text-foreground-800">contact@hitozuma-guide.com</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 rounded-lg bg-background-100">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-accent-600 w-4 h-4 flex items-center justify-center"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">地址</p>
                  <p className="text-sm font-medium text-foreground-800">日本东京都</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 rounded-lg bg-background-100">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-accent-600 w-4 h-4 flex items-center justify-center"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">工作时间</p>
                  <p className="text-sm font-medium text-foreground-800">周一至周五 9:00-18:00</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}